import * as THREE from 'three';
import { DECAL_TTL_MS, EDGE_QUALITY, type EdgeQuality } from '../config/constants';
import { spawnHitDecal } from './decal';
import { HitAudio } from './audio';
import { pulseControllerHaptic } from './haptic';

/**
 * A confirmed impact, already classified upstream. Discriminated on `kind` so a
 * taglio carries the edge geometry a colpo di punta cannot have.
 *
 * `quality` on a cut is structurally barred from being `'thrust'` — the "must not
 * classify" rule is a compile error here, not a comment.
 */
export type Impact =
  | {
      kind: 'cut';
      /** World contact point (cloned internally by the decal). */
      point: THREE.Vector3;
      /** Already classified: good | ok | flat. Never 'thrust'. */
      quality: Exclude<EdgeQuality, 'thrust'>;
      /** Blade edge (filo) direction at contact — drives the coloured filo leaf. */
      edgeDir: THREE.Vector3;
      /** Cut trajectory ⟂ to the blade; null when it has no usable frontal projection. */
      cutDir: THREE.Vector3 | null;
      /** Swing-arc length (m) that led to the cut; scales the white slash. Omitted when
       *  unknown (arcade cut), where the decal falls back to a default slash length. */
      cutLengthM?: number;
      /** Tip speed (m/s) — scales haptic intensity and audio gain. */
      speed: number;
    }
  | {
      kind: 'thrust';
      point: THREE.Vector3;
      speed: number;
    };

/** Minimal audio contract this module fires-and-forgets. Satisfied by {@link HitAudio}. */
export type ImpactAudio = {
  init(): Promise<void>;
  play(quality: EdgeQuality, tipSpeedMPerSec: number): void;
};

/** Haptic dispatch shape. Matches {@link pulseControllerHaptic}'s first two params. */
export type HapticFn = (source: XRInputSource | null | undefined, tipSpeedMPerSec: number) => void;

export type ImpactFeedbackOptions = {
  /** Decal parent (the scene). */
  scene: THREE.Object3D;
  /** Viewer camera — the decal billboards toward it. */
  camera: THREE.Object3D;
  /** Decal persistence policy for this session, fixed for the instance's life:
   *  `'persist'` (free mode — decals stay until cleared) | `'ttl'` (fade after DECAL_TTL_MS). */
  lifetime: 'persist' | 'ttl';
  /** The current sword-hand input source. Read on every `fire`, so a controller swap
   *  mid-session is honored without re-wiring this module. */
  swordInputSource: () => XRInputSource | null;
  /** Audio sink. Defaults to a real {@link HitAudio}; inject a fake in tests. */
  audio?: ImpactAudio;
  /** Haptic dispatch. Defaults to {@link pulseControllerHaptic}; inject a fake in tests. */
  haptic?: HapticFn;
};

/** Thrusts have no edge; the ring decal ignores edgeDir, so any fixed up-vector serves. */
const THRUST_EDGE_DIR = new THREE.Vector3(0, 1, 0);

/**
 * The transient reaction to a confirmed impact: spawn a decal, pulse the sword-hand
 * haptic, play the audio sample. Presentation-only — it never classifies.
 *
 * The wrist HUD is deliberately NOT here: it is a persistent per-frame display on the
 * off-hand, not a one-shot impact response. Decal lifetime (`updateDecals`/`clearDecals`)
 * also stays where it is — driven globally from the render loop, not per impact.
 */
export class ImpactFeedback {
  private readonly scene: THREE.Object3D;
  private readonly camera: THREE.Object3D;
  private readonly ttlMs: number;
  private readonly getInputSource: () => XRInputSource | null;
  private readonly audio: ImpactAudio;
  private readonly haptic: HapticFn;

  constructor(opts: ImpactFeedbackOptions) {
    this.scene = opts.scene;
    this.camera = opts.camera;
    this.ttlMs = opts.lifetime === 'persist' ? Infinity : DECAL_TTL_MS;
    this.getInputSource = opts.swordInputSource;
    this.audio = opts.audio ?? new HitAudio();
    this.haptic = opts.haptic ?? pulseControllerHaptic;
  }

  /** Warm up the audio assets. Idempotent, never throws (missing assets degrade silent). */
  async init(): Promise<void> {
    await this.audio.init();
  }

  /**
   * Fire the trio for one impact. Safe to call before {@link init} resolves (audio
   * simply no-ops). The haptic always targets the current sword-hand source.
   */
  fire(impact: Impact): void {
    const quality: EdgeQuality = impact.kind === 'cut' ? impact.quality : EDGE_QUALITY.thrust;
    const edgeDir = impact.kind === 'cut' ? impact.edgeDir : THRUST_EDGE_DIR;
    const cutDir = impact.kind === 'cut' ? impact.cutDir : null;
    const cutLengthM = impact.kind === 'cut' ? (impact.cutLengthM ?? null) : null;

    spawnHitDecal(
      this.scene,
      impact.point,
      quality,
      this.camera,
      edgeDir,
      cutDir,
      this.ttlMs,
      cutLengthM,
    );
    this.haptic(this.getInputSource(), impact.speed);
    this.audio.play(quality, impact.speed);
  }
}
